/**
 * Calendar-date helpers on `YYYY-MM-DD` strings (no time zone: a calendar date is the same
 * everywhere). The year is always exactly four digits and stays in [MIN_YEAR, MAX_YEAR],
 * mirroring the backend's accepted range.
 */
export const MIN_YEAR = 2000;
export const MAX_YEAR = 2100;

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const CLOCK = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** A real calendar date, four-digit year, inside the supported range. */
export function isIsoDate(value: string): boolean {
  const match = ISO_DATE.exec(value);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (year < MIN_YEAR || year > MAX_YEAR) return false;
  const check = new Date(Date.UTC(year, month - 1, day));
  check.setUTCFullYear(year); // years 0-99 would otherwise map to 1900-1999
  return check.getUTCMonth() === month - 1 && check.getUTCDate() === day;
}

/** `HH:MM`, 24-hour. */
export function isClock(value: string): boolean {
  return CLOCK.test(value);
}

/** A local-time `Date` (midnight) for a valid ISO date, else `undefined`. */
export function parseIsoDate(value: string): Date | undefined {
  if (!isIsoDate(value)) return undefined;
  const [year = 0, month = 1, day = 1] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function toIsoDate(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Shifts an ISO date by whole days, staying inside the supported range. */
export function addDays(value: string, days: number): string {
  const date = parseIsoDate(value);
  if (!date) return value;
  const shifted = new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
  const result = toIsoDate(shifted);
  if (!isIsoDate(result)) return days < 0 ? `${MIN_YEAR}-01-01` : `${MAX_YEAR}-12-31`;
  return result;
}

/**
 * The 7-day week containing `value` (inclusive ISO dates). `firstDay` is the first weekday,
 * 0 = Monday ... 6 = Sunday (Monday by default).
 */
export function weekOf(value: string, firstDay = 0): { first: string; last: string } {
  const date = parseIsoDate(value);
  if (!date) return { first: value, last: value };
  const sinceFirst = (date.getDay() + 6 - firstDay) % 7;
  const first = addDays(value, -sinceFirst);
  return { first, last: addDays(first, 6) };
}

/** Whole days from `from` to `to` (negative when `to` is earlier). */
export function daysBetween(from: string, to: string): number {
  const a = parseIsoDate(from);
  const b = parseIsoDate(to);
  if (!a || !b) return 0;
  return Math.round(
    (Date.UTC(b.getFullYear(), b.getMonth(), b.getDate()) -
      Date.UTC(a.getFullYear(), a.getMonth(), a.getDate())) /
      86_400_000,
  );
}

/** The ISO date `months` months after `value`, the day clamped to the target month's length. */
export function addMonths(value: string, months: number): string {
  const date = parseIsoDate(value);
  if (!date) return value;
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  const result = toIsoDate(
    new Date(target.getFullYear(), target.getMonth(), Math.min(date.getDate(), lastDay)),
  );
  if (!isIsoDate(result)) return months < 0 ? `${MIN_YEAR}-01-01` : `${MAX_YEAR}-12-31`;
  return result;
}

/** Today's calendar date in an IANA zone (falls back to the browser's zone). */
export function todayIn(timeZone?: string | null, now: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      ...(timeZone && { timeZone }),
    }).format(now);
    if (isIsoDate(parts)) return parts;
  } catch {
    // Unknown zone: use the browser's.
  }
  return toIsoDate(now);
}
